import type { PlayerInfo } from '@halvard/protocol';
import type { ice } from '@halvard/rules';

interface Players {
  host: PlayerInfo | null;
  guest: PlayerInfo | null;
}

function nameOf(players: Players, seat: ice.Seat, me: ice.Seat | null): string {
  if (seat === me) return 'You';
  return players[seat]?.name ?? (seat === 'host' ? 'The host' : 'Your opponent');
}

/** "Guest 4F2A reached 20 Glory in round 14" / "You were held to 17 Glory". */
export function attackText(game: ice.IceMatchGame, players: Players, me: ice.Seat | null): string {
  const who = nameOf(players, game.attacker, me);
  if (game.reason === 'resign') {
    const quitter = game.winner === 'brand' ? 'the defender' : 'the attacker';
    return `${who} attacked; ${quitter} resigned in round ${game.round}`;
  }
  if (game.winner === 'brand') {
    return game.reason === 'sunk'
      ? `${who} sank every defender in round ${game.round}`
      : `${who} reached ${game.glory} Glory in round ${game.round}`;
  }
  const were = who === 'You' ? 'were' : 'was';
  return game.reason === 'sunk'
    ? `${who} lost every warrior in round ${game.round}`
    : `${who} ${were} held to ${game.glory} Glory`;
}

/** The match result, from the point of view of `me` (null for spectators). */
export function outcomeLine(
  outcome: ice.IceMatchOutcome,
  games: readonly ice.IceMatchGame[],
  players: Players,
  me: ice.Seat | null,
): { title: string; detail: string } {
  const title =
    outcome.winner === null
      ? 'The match is drawn'
      : outcome.winner === me
        ? 'You win the match'
        : me
          ? 'You lose the match'
          : `${players[outcome.winner]?.name ?? 'The winner'} wins the match`;
  const [a, b] = games;
  switch (outcome.reason) {
    case 'resign':
      return { title, detail: 'A player resigned.' };
    case 'attack':
      return {
        title,
        detail: b ? 'Only one attack took the runestones.' : 'The game decided it.',
      };
    case 'faster':
      return {
        title,
        detail:
          a && b && a.round !== b.round
            ? `Both attacks succeeded; the faster one won (round ${Math.min(a.round, b.round)} against round ${Math.max(a.round, b.round)}).`
            : 'Both attacks succeeded in the same round; more Glory won.',
      };
    case 'glory':
      return {
        title,
        detail:
          a && b
            ? `Both attacks were held; more Glory won (${Math.max(a.glory, b.glory)} against ${Math.min(a.glory, b.glory)}).`
            : 'More Glory won.',
      };
    case 'level':
      return { title, detail: 'Nothing separated the two attacks.' };
  }
}
